import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { businessDaysBetween } from '@/lib/datetime';
import { resolveEmployeeLabel, searchEmployees } from '@/features/hrm/employees/select';
import { LEAVE_TYPES } from '../constants';
import type { TimeOffInput } from '../types';

const schema = z
  .object({
    employee: z.string().min(1, 'Employee is required'),
    type: z.enum(['vacation', 'sick', 'unpaid', 'other']),
    start_date: z.string().min(1, 'Start is required'),
    end_date: z.string().min(1, 'End is required'),
    days: z
      .string()
      .refine((v) => !Number.isNaN(Number(v)) && Number(v) > 0, 'Enter days (> 0)'),
    reason: z.string().max(255),
  })
  .refine((v) => v.end_date >= v.start_date, {
    message: 'End must be on or after the start.',
    path: ['end_date'],
  });

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['employee', 'type', 'start_date', 'end_date', 'days', 'reason'] as const;

interface TimeOffFormProps {
  defaultValues?: Partial<FormValues>;
  submitLabel: string;
  onSubmit: (input: TimeOffInput) => Promise<unknown>;
  onCancel: () => void;
}

export function TimeOffForm({ defaultValues, submitLabel, onSubmit, onCancel }: TimeOffFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee: '',
      type: 'vacation',
      start_date: '',
      end_date: '',
      days: '1',
      reason: '',
      ...defaultValues,
    },
  });


  // Auto-fill days as the inclusive Mon–Fri count whenever the range changes;
  // the field stays editable for half-days / org-specific adjustments.
  const startDate = useWatch({ control: form.control, name: 'start_date' });
  const endDate = useWatch({ control: form.control, name: 'end_date' });
  useEffect(() => {
    if (!startDate || !endDate) return;
    const days = businessDaysBetween(startDate, endDate);
    if (days > 0) form.setValue('days', String(days), { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form is stable
  }, [startDate, endDate]);

  async function handleSubmit(values: FormValues) {
    const input: TimeOffInput = {
      employee: Number(values.employee),
      type: values.type,
      start_date: values.start_date,
      end_date: values.end_date,
      days: values.days,
      reason: values.reason.trim(),
    };
    try {
      await onSubmit(input);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
        <FormSection variant="card" title="Request" description="Who is taking time off, and when?">
          <div className="grid gap-6 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="employee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="time-off-employee-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchEmployees}
                      resolveLabel={resolveEmployeeLabel}
                      placeholder="Select an employee"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LEAVE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-6 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="start_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Days</FormLabel>
                  <FormControl>
                    <Input type="number" min="0" step="0.5" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection variant="card" title="Details" description="Why are you requesting?">
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
